import { Head, router, usePage } from '@inertiajs/react';
import { Loader2, RadioTower, Settings, Share2, Tv } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { AdminMiscObsControlShow } from '@/types';
import { SettingsDialog } from './components/SettingsDialog';
import {
  DEFAULT_OBS_SETTINGS,
  type ObsSettings,
  useObs,
} from './components/useObs';

const PATH = '/admin/misc/obs-control';
const STORAGE_KEY = 'obs-control-settings';

function loadSettings(): ObsSettings {
  if (typeof window === 'undefined') return DEFAULT_OBS_SETTINGS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_OBS_SETTINGS;
    return { ...DEFAULT_OBS_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_OBS_SETTINGS;
  }
}

function persistSettings(settings: ObsSettings) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // ignore quota / private mode errors
  }
}

export default function Show({ title }: AdminMiscObsControlShow) {
  const { url } = usePage();

  const [settings, setSettings] = useState<ObsSettings>(DEFAULT_OBS_SETTINGS);
  const [hydrated, setHydrated] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const autoConnectedRef = useRef(false);

  const {
    status,
    error,
    scenes,
    currentScene,
    frame,
    connect,
    disconnect,
    switchScene,
  } = useObs(settings);

  // Hydrate from localStorage + consume any shared query params on first mount.
  useEffect(() => {
    const stored = loadSettings();
    const searchParams = new URLSearchParams(url.split('?')[1] ?? '');

    const qpIp = searchParams.get('ip');
    const qpPort = searchParams.get('port');
    const qpPassword = searchParams.get('password');
    const qpFps = searchParams.get('fps');
    const qpWidth = searchParams.get('width');
    const qpQuality = searchParams.get('quality');

    const hasShared =
      qpIp !== null ||
      qpPort !== null ||
      qpPassword !== null ||
      qpFps !== null ||
      qpWidth !== null ||
      qpQuality !== null;

    const next: ObsSettings = {
      ...stored,
      ...(qpIp !== null ? { ip: qpIp.trim() } : {}),
      ...(qpPort !== null ? { port: qpPort.trim() } : {}),
      ...(qpPassword !== null ? { password: qpPassword } : {}),
      ...(qpFps !== null
        ? { previewFps: Number(qpFps) || stored.previewFps }
        : {}),
      ...(qpWidth !== null
        ? { previewWidth: Number(qpWidth) || stored.previewWidth }
        : {}),
      ...(qpQuality !== null
        ? { previewQuality: Number(qpQuality) || stored.previewQuality }
        : {}),
    };

    setSettings(next);
    setHydrated(true);

    if (hasShared) {
      persistSettings(next);
      // Consume the query params so the password isn't left in the URL/history.
      router.visit(PATH, {
        replace: true,
        preserveState: true,
        preserveScroll: true,
      });
      toast.success('Loaded OBS settings from the shared link.');
    }
  }, []);

  // Auto-connect once after hydration if we have a host.
  useEffect(() => {
    if (!hydrated || autoConnectedRef.current) return;
    autoConnectedRef.current = true;
    if (settings.ip) {
      void connect();
    } else {
      setSettingsOpen(true);
    }
  }, [hydrated]);

  const handleSave = useCallback(
    (next: ObsSettings) => {
      setSettings(next);
      persistSettings(next);
      void connect();
    },
    [connect],
  );

  const handleShare = useCallback(async () => {
    if (!settings.ip) {
      toast.error('Nothing to share — set the host IP first.');
      return;
    }
    const params = new URLSearchParams({
      ip: settings.ip,
      port: settings.port,
    });
    if (settings.password) params.set('password', settings.password);
    params.set('fps', String(settings.previewFps));
    params.set('width', String(settings.previewWidth));
    params.set('quality', String(settings.previewQuality));
    const shareUrl = `${window.location.origin}${PATH}?${params.toString()}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied to clipboard.');
    } catch {
      toast.error('Could not copy. Link logged to console.');
      console.log(shareUrl);
    }
  }, [settings]);

  const statusMeta = useMemo(() => {
    switch (status) {
      case 'connected':
        return { label: 'Connected', dot: 'bg-green-500' };
      case 'connecting':
        return { label: 'Connecting…', dot: 'bg-yellow-500 animate-pulse' };
      case 'error':
        return { label: 'Error', dot: 'bg-red-500' };
      default:
        return { label: 'Offline', dot: 'bg-muted-foreground' };
    }
  }, [status]);

  const isConnected = status === 'connected';

  return (
    <>
      <Head title={title} />
      <div className="mx-auto flex max-w-4xl flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <Tv className="size-6" /> OBS Control
            </h1>
            <p className="text-muted-foreground text-sm">
              {settings.ip
                ? `Target: ws://${settings.ip}:${settings.port || '4455'}`
                : 'No OBS host configured yet.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="gap-1.5 py-1">
              <span className={cn('size-2 rounded-full', statusMeta.dot)} />
              {statusMeta.label}
            </Badge>
            <Button variant="outline" size="sm" onClick={handleShare}>
              <Share2 className="size-4" /> Share
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSettingsOpen(true)}
            >
              <Settings className="size-4" /> Settings
            </Button>
          </div>
        </div>

        {error && (
          <div className="rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-red-600 text-sm dark:text-red-400">
            {error}
          </div>
        )}

        <div className="relative aspect-video w-full overflow-hidden rounded-xl border border-border bg-black">
          {frame ? (
            <img
              src={frame}
              alt="OBS program preview"
              className="size-full object-contain"
            />
          ) : (
            <div className="flex size-full items-center justify-center text-muted-foreground text-sm">
              {isConnected ? 'Waiting for preview…' : 'No preview'}
            </div>
          )}
          {isConnected && (
            <span className="absolute top-3 left-3 flex items-center gap-1.5 rounded bg-red-600 px-2 py-1 font-bold text-[11px] text-white tracking-wide">
              <span className="size-1.5 animate-pulse rounded-full bg-white" />{' '}
              LIVE
            </span>
          )}
        </div>

        {currentScene && (
          <p className="text-center text-muted-foreground text-sm">
            Program: <strong className="text-foreground">{currentScene}</strong>
          </p>
        )}

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {scenes.map((name) => {
            const active = name === currentScene;
            return (
              <Button
                key={name}
                variant={active ? 'default' : 'outline'}
                disabled={!isConnected}
                onClick={() => switchScene(name)}
                className={cn(
                  'h-16 justify-start whitespace-normal text-left font-semibold text-base',
                  active && 'ring-2 ring-red-500',
                )}
              >
                <RadioTower className="size-4 shrink-0" />
                <span className="truncate">{name}</span>
              </Button>
            );
          })}
        </div>

        {scenes.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-10 text-center text-muted-foreground text-sm">
            {status === 'connecting' ? (
              <Loader2 className="size-6 animate-spin" />
            ) : null}
            <p>
              {isConnected
                ? 'No scenes found in OBS.'
                : status === 'connecting'
                  ? 'Connecting to OBS…'
                  : 'Not connected. Open settings to configure the OBS host.'}
            </p>
            {!isConnected && status !== 'connecting' && (
              <div className="flex gap-2">
                <Button
                  onClick={() =>
                    settings.ip ? connect() : setSettingsOpen(true)
                  }
                >
                  {settings.ip ? 'Connect' : 'Configure'}
                </Button>
              </div>
            )}
          </div>
        )}

        {isConnected && (
          <div className="flex justify-center">
            <Button variant="ghost" size="sm" onClick={() => disconnect()}>
              Disconnect
            </Button>
          </div>
        )}

        <SettingsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          settings={settings}
          onSave={handleSave}
        />
      </div>
    </>
  );
}
